import type { MouseEventHandler } from 'react'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { LocationIcon } from '@frontend/pole-ui/lib/components/Icon/svg/LocationIcon'
import classNames from 'classnames'
import { openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { Button, Loader, TextWithIcon } from '@/components/ui'
import { DatePicker, Dropdown, SliderValueMoney } from '@/components/ui/ReactHookFormFields'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { DEFAULT_ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT } from '@/pages/AdvancePaymentOnFutureHarvest/components/AdvancePaymentCalculator/hooks/useAdvancePaymentCalculatorForm/constants'
import { pushGtmEventOnChangeCalculate } from '@/pages/AdvancePaymentOnFutureHarvest/gtm-helpers'
import type { RootState } from '@/redux/interfaces'
import { formatLocalizedString } from '@/utils/formatUtils'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { formatMoney } from '@/utils/price'
import type { TAdvancePaymentCalculatorElevator } from '../../types'
import { AdvancePaymentCalculatorMap } from '../AdvancePaymentCalculatorMap'
import { DaysUntilHarvestDelivery } from './components/DaysUntilHarvestDelivery'
import { OutsideLabel } from './components/OutsideLabel'
import { ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT_RANGE } from './constants'
import {
  isOutOfValidAdvancePaymentDateRange,
  isOutOfValidProductDeliveryDateRange,
  outOfValidProductDeliveryDateRangeRule,
} from './helpers'
import { messages } from './messages'
import type { TAdvancePaymentCalculatorFormProps } from './types'
import './styles.scss'

addModalToWellKnown({
  AdvancePaymentCalculatorMap,
})

const mapState = (state: RootState) => ({
  userGtmInfo: getUserGtmInfo()(state),
})

export const AdvancePaymentCalculatorForm: React.FC<TAdvancePaymentCalculatorFormProps> = props => {
  const { className, daysUntilHarvestDelivery, form, elevators, onSubmit, onOpenElevatorDropdown } = props

  const { userGtmInfo } = useSelector(mapState)
  const { userRegion } = useUserRegion()

  useEffect(() => {
    if (daysUntilHarvestDelivery) {
      pushGtmEventOnChangeCalculate(userGtmInfo, userRegion.name, `Срок-${daysUntilHarvestDelivery}`)
    }
  }, [daysUntilHarvestDelivery, userGtmInfo, userRegion.name])

  const intl = useIntl()
  const dispatch = useDispatch()
  const { isDesktop } = useDeviceType()

  const elevatorOptions = useMemo(
    () =>
      (elevators ?? [])
        .filter(elevator => elevator.isAvailableForCalculation)
        .map(elevator => ({ label: elevator.name, value: elevator.id, payload: elevator })),
    [elevators],
  )

  const openMap = (initialElevator?: TAdvancePaymentCalculatorElevator) => {
    dispatch(
      openModal({
        options: { dialogId: 'AdvancePaymentCalculatorMap' },
        contentOptions: {
          initialElevator,
          onSelectElevator: (elevator: TAdvancePaymentCalculatorElevator) => {
            form.setValue('elevator', elevator?.id ?? null)
          },
        },
      }),
    )
  }

  const handleClickOpenOnMap =
    (elevator?: TAdvancePaymentCalculatorElevator): MouseEventHandler =>
    event => {
      event.stopPropagation()
      openMap(elevator)
    }

  const handleChangeDateRangeField = () => {
    form.trigger(['advancePaymentDate', 'productDeliveryDate'])
  }

  const advancePaymentDate = form.watch('advancePaymentDate')

  const isDatesOutOfRange =
    form.formState.errors.advancePaymentDate?.type === 'validate' || form.formState.errors.productDeliveryDate?.type === 'validate'

  const dateRangeFieldErrorProps = { ...(isDatesOutOfRange ? { errorText: '' } : {}) }

  const settingsSliderAdvancedAmount = useMemo(
    () => ({
      min: ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT_RANGE.minValue,
      max: ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT_RANGE.maxValue,
      step: ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT_RANGE.step,
      defaultValue: DEFAULT_ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT,
    }),
    [],
  )

  return (
    <form onSubmit={onSubmit} className={classNames(className, 'advancePaymentCalculatorForm')}>
      <OutsideLabel text={messages.field.advanceAmount.label}>
        <SliderValueMoney
          className="advancePaymentCalculatorForm__advanceAmount"
          name="advanceAmount"
          settings={settingsSliderAdvancedAmount}
          control={form.control}
          onChange={value => pushGtmEventOnChangeCalculate(userGtmInfo, userRegion.name, `Размер-${value}`)}
          rules={{
            required: messages.error.required,
            min: {
              value: String(ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT_RANGE.minValue),
              message: formatLocalizedString(messages.field.advanceAmount.error.min, {
                minValue: formatMoney(String(ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT_RANGE.minValue), intl),
              }),
            },
            max: {
              value: String(ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT_RANGE.maxValue),
              message: formatLocalizedString(messages.field.advanceAmount.error.max, {
                maxValue: formatMoney(String(ADVANCE_PAYMENT_CALCULATOR_ADVANCE_AMOUNT_RANGE.maxValue), intl),
              }),
            },
          }}
          pointsCount={isDesktop ? 5 : 3}
        />

        {form.formState.errors.advanceAmount && (
          <span className="advancePaymentCalculatorForm__advanceAmountError text_super-small">
            {form.formState.errors.advanceAmount.message}
          </span>
        )}
      </OutsideLabel>
      <div className="advancePaymentCalculatorForm__elevatorBlock">
        <OutsideLabel className="advancePaymentCalculatorForm__elevator" text={messages.field.elevator.label}>
          <Dropdown
            name="elevator"
            options={elevatorOptions}
            placeholder={messages.field.elevator.placeholder}
            control={form.control}
            modifiers={['white']}
            rules={{
              required: messages.error.required,
            }}
            Menu={({ menu }) => {
              if (!elevators) {
                return (
                  <div className="advancePaymentCalculatorForm__elevatorLoader">
                    <Loader isVisible />
                  </div>
                )
              }

              return <>{menu}</>
            }}
            MenuItem={option => {
              const elevator = option.item.payload as TAdvancePaymentCalculatorElevator | undefined

              if (!elevator) {
                return null
              }

              return (
                <div className="advancePaymentCalculatorForm__elevatorOption">
                  <div className="text_small">{elevator.name}</div>
                  <div className="advancePaymentCalculatorForm__elevatorOptionAddress text_super-small">{elevator.address}</div>
                  <Button className="advancePaymentCalculatorForm__elevatorOptionOnMap" isUnstyled onClick={handleClickOpenOnMap(elevator)}>
                    <TextWithIcon
                      className="text_super-small"
                      text={messages.field.elevator.button.onMap}
                      textClassName="advancePaymentCalculatorForm__elevatorOptionLink underline underline_green"
                      textModifier="super-small"
                      iconProps={{
                        className: 'advancePaymentCalculatorForm__elevatorOptionOnMapIcon',
                        component: <LocationIcon />,
                      }}
                    />
                  </Button>
                </div>
              )
            }}
            isHangingError
            isRequired
            onOpen={onOpenElevatorDropdown}
          />
        </OutsideLabel>
        <Button
          className="advancePaymentCalculatorForm__openMap"
          modifiers={['outline']}
          iconProps={{
            className: 'advancePaymentCalculatorForm__openMapIcon',
            component: <LocationIcon />,
          }}
          onClick={handleClickOpenOnMap()}
        >
          {messages.button.openMap}
        </Button>
      </div>
      <div className="advancePaymentCalculatorForm__dates">
        <OutsideLabel text={messages.field.advancePaymentDate.label}>
          <DatePicker
            className="advancePaymentCalculatorForm__advancePaymentDate"
            name="advancePaymentDate"
            placeholder={messages.field.advancePaymentDate.placeholder}
            control={form.control}
            rules={{
              required: messages.error.required,
              validate: outOfValidProductDeliveryDateRangeRule,
            }}
            pickerProps={{
              disabledDate: isOutOfValidAdvancePaymentDateRange,
            }}
            onChange={handleChangeDateRangeField}
            isRequired
            {...dateRangeFieldErrorProps}
          />
        </OutsideLabel>
        <OutsideLabel text={messages.field.productDeliveryDate.label}>
          <DatePicker
            className="advancePaymentCalculatorForm__productDeliveryDate"
            name="productDeliveryDate"
            placeholder={messages.field.productDeliveryDate.placeholder}
            control={form.control}
            isDisabled={!advancePaymentDate}
            rules={{
              required: messages.error.required,
              validate: outOfValidProductDeliveryDateRangeRule,
            }}
            pickerProps={{
              disabledDate: isOutOfValidProductDeliveryDateRange(advancePaymentDate),
            }}
            onChange={handleChangeDateRangeField}
            isRequired
            {...dateRangeFieldErrorProps}
          />
        </OutsideLabel>
        <OutsideLabel
          className="advancePaymentCalculatorForm__daysUntilHarvestDelivery"
          text={messages.field.daysUntilHarvestDelivery.label}
        >
          <DaysUntilHarvestDelivery days={daysUntilHarvestDelivery} />
        </OutsideLabel>
        {isDatesOutOfRange && (
          <div className="advancePaymentCalculatorForm__datesOutOfRangeError text_super-small">{messages.error.datesOutOfRange}</div>
        )}
      </div>
    </form>
  )
}
